/**
 * Submission progress model.
 *
 * Pure, presentation-agnostic description of the pre-checkout submission
 * pipeline. Stages map 1:1 to REAL operations in `/submit` — nothing here
 * advances on a timer, and no percentage is ever derived. A stage is only
 * marked complete when its underlying operation actually succeeded.
 */

export type SubmitStageId =
  | "securing"
  | "creating"
  | "uploading"
  | "saving"
  | "ready";

export interface SubmitStageDef {
  id: SubmitStageId;
  /** Present-tense label shown in the progress panel while active. */
  label: string;
  /** Past-tense label shown once the underlying operation succeeded. */
  doneLabel: string;
  /** Button copy while this stage is active. */
  buttonLabel: string;
  /** Live status line shown above the checklist while this stage runs. */
  statusMessage: string;
  /** Reassuring helper line shown beneath the ACTIVE stage only. */
  helperText: string;
}

export const SUBMIT_STAGES: SubmitStageDef[] = [
  {
    id: "securing",
    label: "Securing your invoice",
    doneLabel: "Invoice secured",
    buttonLabel: "Securing Invoice…",
    statusMessage: "Securing your invoice…",
    helperText: "Checking your invoice and protecting your submission…",
  },
  {
    id: "creating",
    label: "Creating your review",
    doneLabel: "Review created",
    buttonLabel: "Creating Review…",
    statusMessage: "Creating your review…",
    helperText: "Setting up your review file…",
  },
  {
    id: "uploading",
    label: "Uploading invoice files",
    doneLabel: "Files uploaded",
    buttonLabel: "Uploading Files…",
    statusMessage: "Uploading your invoice…",
    helperText: "Transferring your files securely…",
  },
  {
    id: "saving",
    label: "Saving submission details",
    doneLabel: "Submission details saved",
    buttonLabel: "Saving Details…",
    statusMessage: "Saving your submission…",
    helperText: "Saving your information securely…",
  },
  {
    id: "ready",
    label: "Preparing secure checkout",
    doneLabel: "Review prepared",
    buttonLabel: "Preparing Checkout…",
    statusMessage: "Preparing secure checkout…",
    helperText: "Preparing your secure checkout…",
  },
];

/** Title shown the moment every stage has genuinely completed. */
export const COMPLETION_TITLE = "Review successfully prepared";

/** Sub-line shown with the completion title, immediately before the
 *  payment step is presented. */
export const COMPLETION_MESSAGE = "Taking you to secure payment…";

/**
 * How long the completion confirmation stays on screen before the payment
 * step is shown. This is a readability hold on an ALREADY-FINISHED
 * submission — it never delays or gates any real operation.
 */
export const COMPLETION_HOLD_MS = 400;

/** Label prefix for the elapsed clock (never a countdown or an ETA). */
export const ELAPSED_LABEL = "Time Elapsed";


export function stageIndex(id: SubmitStageId): number {
  return SUBMIT_STAGES.findIndex((s) => s.id === id);
}

export function stageDef(id: SubmitStageId): SubmitStageDef {
  return SUBMIT_STAGES[stageIndex(id)];
}

export type StageState = "complete" | "active" | "upcoming";

/** Stage status derived from the active stage; never from elapsed time. */
export function stageStateFor(
  id: SubmitStageId,
  active: SubmitStageId | null,
  finished: boolean,
): StageState {
  if (finished) return "complete";
  if (!active) return "upcoming";
  const i = stageIndex(id);
  const a = stageIndex(active);
  if (i < a) return "complete";
  if (i === a) return "active";
  return "upcoming";
}

/** Show a gentle "taking longer than usual" notice; never cancels anything. */
export const STALL_NOTICE_MS = 20_000;

/** Hard ceiling before we surface a recoverable error and re-enable the form. */
export const SUBMIT_TIMEOUT_MS = 120_000;

export const STALL_NOTICE =
  "This is taking a little longer than usual. Your invoice is still being processed.";

export type SubmitErrorKind =
  | "upload"
  | "review_create"
  | "persist"
  | "checkout"
  | "network"
  | "auth"
  | "timeout"
  | "unknown";

const ERROR_MESSAGES: Record<SubmitErrorKind, string> = {
  upload:
    "We couldn't upload your invoice files. Your details are still saved. Please try again.",
  review_create:
    "We couldn't create your review. Your invoice and details are still saved. Please try again.",
  persist:
    "We couldn't save your submission details. Your invoice is still saved. Please try again.",
  checkout:
    "We couldn't open secure checkout. Your review is saved — please try again.",
  network:
    "Your connection was interrupted. Your invoice and details are still saved. Please try again.",
  auth: "Your session expired. Please sign in again — your invoice and details are still saved.",
  timeout:
    "We couldn't finish preparing your review. Your invoice and details are still saved. Please try again.",
  unknown:
    "We couldn't finish preparing your review. Your invoice and details are still saved. Please try again.",
};

export function submitErrorMessage(kind: SubmitErrorKind): string {
  return ERROR_MESSAGES[kind];
}

/** Best-effort, non-sensitive classification of a thrown value. */
export function classifySubmitError(
  error: unknown,
  stage: SubmitStageId | null,
): SubmitErrorKind {
  const raw =
    error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  const text = raw.toLowerCase();
  if (
    text.includes("jwt") ||
    text.includes("unauthorized") ||
    text.includes("401") ||
    text.includes("session")
  )
    return "auth";
  if (
    text.includes("failed to fetch") ||
    text.includes("networkerror") ||
    text.includes("network request failed") ||
    text.includes("offline")
  )
    return "network";
  switch (stage) {
    case "creating":
      return "review_create";
    case "uploading":
      return "upload";
    case "saving":
      return "persist";
    case "ready":
      return "checkout";
    default:
      return "unknown";
  }
}

/** "8 seconds" / "1 minute 4 seconds" — whole units only, never a percentage. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  const secPart = `${secs} second${secs === 1 ? "" : "s"}`;
  if (!mins) return secPart;
  const minPart = `${mins} minute${mins === 1 ? "" : "s"}`;
  return secs ? `${minPart} ${secPart}` : minPart;
}

/** Safe, PII-free diagnostic record written to the console/monitoring. */
export interface SubmitDiagnostic {
  stage: SubmitStageId | null;
  code: SubmitErrorKind;
  reviewId: string | null;
  requestId: string;
  timestamp: string;
}

export function buildSubmitDiagnostic(input: {
  stage: SubmitStageId | null;
  code: SubmitErrorKind;
  reviewId: string | null;
  requestId: string;
}): SubmitDiagnostic {
  return { ...input, timestamp: new Date().toISOString() };
}

export function newSubmitRequestId(): string {
  const g = globalThis.crypto;
  if (g && "randomUUID" in g) return g.randomUUID();
  return `req_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}
